import "@web-components/cart";
import { Effect, Schema, Queue } from "effect";
import { AddToCart } from "@/lib/effect/forms";
import {
  AddToCartEvents,
  withAddToCartEvents,
  AddToCartError,
  isAddToCartSubmitEvent,
  CartRuntime,
} from "@/lib/effect/cart";
import AjaxCart from "@brytdesigns/shopify-ajax-cart";
import "@web-components/add-to-cart-action";

type AddToCartData = typeof AddToCart.FormDataSchema.Type;

const addToCartEffect = (data: AddToCartData) =>
  Effect.tryPromise({
    try: () => {
      return AjaxCart.addItems(Array.isArray(data) ? data : [data]);
    },
    catch: (error) =>
      error instanceof Error
        ? error
        : new AddToCartError({
          message: "Failed to add item to cart",
          cause: error,
        }),
  }).pipe(
    Effect.withSpan("cart/addToCartEffect", {
      attributes: {
        ...data,
      },
    }),
  );

const preventDefault = (event: Event) =>
  Effect.sync(() => event.preventDefault());

const onSubmitEventEffect = Effect.fn("cart/onSubmitEventEffect")(
  function* (event: SubmitEvent) {
    if (!isAddToCartSubmitEvent(event)) {
      return Effect.none;
    }
    yield* Effect.log("Add to cart event received!");
    yield* preventDefault(event);
    const formData = new FormData(event.target as HTMLFormElement);
    const decodedFormData = yield* Schema.decode(AddToCart.FormDataSchema)(
      formData,
    );

    yield* Effect.annotateLogsScoped({
      input: decodedFormData,
    });

    yield* Effect.log("Data decoded successfully!");

    const result = yield* withAddToCartEvents(
      event,
      decodedFormData,
      addToCartEffect(decodedFormData),
    );

    yield* Effect.annotateLogsScoped({
      result,
    });

    return result;
  },
  Effect.tapError((error) => Effect.logError(error.message)),
  Effect.scoped,
);

const addGlobalSubmitHandler = Effect.acquireRelease(
  Effect.sync(() => {
    const controller = new AbortController();
    document.addEventListener(
      "submit",
      (event) =>
        CartRuntime.runFork(
          onSubmitEventEffect(event).pipe(
            Effect.tapErrorCause((cause) =>
              Effect.logDebug("submit failed", cause),
            ),
            Effect.ignore,
          ),
        ),
      {
        signal: controller.signal,
      },
    );
    return controller;
  }),
  (controller) => Effect.sync(() => controller.abort()),
);

const updateButton = (
  element: HTMLElement | null,
  { text = "", disabled = false },
  formState: "initial" | "submitting" | "success" | "error" = "initial",
) =>
  Effect.sync(() => {
    if (!element) return;
    if (text) element.textContent = text;
    if (disabled) element.setAttribute("disabled", "disabled");
    else element.removeAttribute("disabled");
    element
      .querySelector("add-to-cart-action")
      ?.setAttribute("form-state", formState);
  });

const disableAllInputs = (form: HTMLFormElement | null) =>
  Effect.sync(() => {
    if (!form) return;
    form
      .querySelectorAll("input, select, textarea, button")
      .forEach((input) => {
        input.setAttribute("disabled", "disabled");
      });
  });

const enableAllInputs = (form: HTMLFormElement | null) =>
  Effect.sync(() => {
    if (!form) return;
    form
      .querySelectorAll("input, select, textarea, button")
      .forEach((input) => {
        input.removeAttribute("disabled");
      });
  });

const startListener = Effect.gen(function* () {
  const events = yield* AddToCartEvents;
  const sub = yield* events.subscribe;

  const loop = Effect.forever(
    Queue.take(sub).pipe(
      Effect.flatMap((event) =>
        Effect.fork(
          Effect.gen(function* () {
            yield* Effect.annotateLogsScoped({
              data: event.data,
              submitter: event.submitter,
            });
            switch (event._tag) {
              case "before": {
                yield* updateButton(
                  event.submitter,
                  {
                    disabled: true,
                  },
                  "submitting",
                );
                yield* disableAllInputs(event.form);
                yield* Effect.logInfo(event._tag);
                break;
              }
              case "success": {
                yield* updateButton(
                  event.submitter,
                  {
                    disabled: true,
                  },
                  "success",
                );
                yield* Effect.annotateLogsScoped({
                  result: event.result,
                });
                yield* Effect.logInfo(event._tag);
                break;
              }
              case "error": {
                yield* updateButton(
                  event.submitter,
                  {
                    disabled: true,
                  },
                  "error",
                );
                yield* Effect.annotateLogsScoped({
                  cause: (event.error as any)?.cause,
                });
                yield* Effect.logError(event._tag);
                break;
              }
              case "finally": {
                yield* Effect.sleep("1 second");

                yield* updateButton(
                  event.submitter,
                  {
                    disabled: false,
                  },
                  "initial",
                );

                yield* enableAllInputs(event.form);

                yield* Effect.logInfo(event._tag);
                break;
              }
            }
          }).pipe(Effect.scoped),
        ),
      ),
    ),
  );

  yield* Effect.fork(loop);
});

const main = Effect.gen(function* () {
  yield* addGlobalSubmitHandler;
  yield* Effect.log("Cart events initialized!");
  yield* startListener;
  yield* Effect.never;
}).pipe(Effect.scoped);

CartRuntime.runPromise(main);
